import { call, put, takeLatest, select, fork } from 'redux-saga/effects';
import sharedSagas from 'store/sharedSagas';
import handleError from 'services/error/handleScopeError';
import SearchConsoleApi from 'services/api/SearchConsoleApi';
import { communicationStatus, HttpStatus } from 'services/utils';
import {
  LIST_STATUS_KEY,
  MASTERDATA_STATUS_KEY,
  SETTING_STATUS_KEY,
} from 'domain/search-console-setting/constants';
import { getPermissions } from 'store/auth/selectors';
import { SETTING_USER_EDIT } from 'domain/permissions/contractPermissions';

import actions from './actions';
import types from './types';
import selectors from './selectors';

const { BAD_REQUEST } = HttpStatus;
const { LOADING, SUCCEEDED } = communicationStatus;

function* handleGetData() {
  yield put(actions.setStatus(LOADING, LIST_STATUS_KEY));
  const sort = yield select(selectors.getSortRequest);
  const {
    data: { data, metadata },
  } = yield call(SearchConsoleApi.fetchData, sort);

  yield put(actions.setStatus(SUCCEEDED, LIST_STATUS_KEY));
  yield put(actions.setData({ data, metadata }));
}

function* handleCreate(action) {
  const { data: dataRequest, callback } = action.payload;
  yield put(actions.setStatus(LOADING, SETTING_STATUS_KEY));
  yield call(SearchConsoleApi.create, dataRequest);
  yield put(actions.setStatus(SUCCEEDED, SETTING_STATUS_KEY));
  callback({ action: action.type });
}

function* handleGetDetail(action) {
  const { id, callback } = action.payload;
  yield put(actions.setStatus(LOADING, SETTING_STATUS_KEY));
  const {
    data: { data },
  } = yield call(SearchConsoleApi.getDetail, id);
  yield put(actions.setStatus(SUCCEEDED, SETTING_STATUS_KEY));
  yield put(actions.setDataSetting(data));
  callback({ action: action.type });
}

function* handleUpdate(action) {
  const { id, data: dataRequest, callback } = action.payload;
  yield put(actions.setStatus(LOADING, SETTING_STATUS_KEY));
  yield call(SearchConsoleApi.update, id, dataRequest);
  yield put(actions.setStatus(SUCCEEDED, SETTING_STATUS_KEY));
  callback({ action: action.type });
}

function* handleGetMasterData() {
  yield put(actions.setStatus(LOADING, MASTERDATA_STATUS_KEY));
  const permissions = yield select(getPermissions);
  const response = {};
  if (permissions.includes(SETTING_USER_EDIT)) {
    const userRes = yield call(SearchConsoleApi.fetchUsers);
    response.users = userRes.data.data.detail;
  }
  yield put(actions.setStatus(SUCCEEDED, MASTERDATA_STATUS_KEY));
  yield put(actions.setMasterData(response));
}

function* changeStatus(action) {
  const { id, status } = action.payload;
  yield put(actions.setStatus(LOADING, LIST_STATUS_KEY));
  yield call(SearchConsoleApi.changeStatus, id, status);
  yield fork(handleGetData);
}

function* handleDelete(action) {
  const { ids, callback } = action.payload;
  yield call(SearchConsoleApi.delete, ids);
  callback({ isError: false });
}

function* errorHandler(err, action) {
  const { callback = () => {} } = action.payload || {};
  const { error, scope } = handleError(err?.response || {}, [BAD_REQUEST]);
  const errors = error?.data?.errors || [];
  switch (action.type) {
    case types.CREATE:
    case types.UPDATE:
    case types.DELETE:
      yield put(actions.setErrorSetting(errors, scope));
      break;
    default:
      break;
  }

  if (
    [types.DELETE, types.CREATE, types.UPDATE, types.GET_DETAIL].includes(
      action.type
    )
  ) {
    callback({ action: action.type, isError: true, errors });
  }

  yield put(actions.setErrors(errors, action.type, scope));
}

export default function* watch() {
  yield takeLatest(
    types.GET_DATA,
    sharedSagas.safe(errorHandler, handleGetData)
  );
  yield takeLatest(
    types.GET_DETAIL,
    sharedSagas.safe(errorHandler, handleGetDetail)
  );
  yield takeLatest(types.CREATE, sharedSagas.safe(errorHandler, handleCreate));
  yield takeLatest(types.UPDATE, sharedSagas.safe(errorHandler, handleUpdate));
  yield takeLatest(
    types.CHANGE_STATUS,
    sharedSagas.safe(errorHandler, changeStatus)
  );
  yield takeLatest(types.DELETE, sharedSagas.safe(errorHandler, handleDelete));
  yield takeLatest(
    types.GET_MASTERDATA,
    sharedSagas.safe(errorHandler, handleGetMasterData)
  );
}
